import { Controller } from "@hotwired/stimulus";

// Drives the sales opportunity "Deal Assistant" chat: submit-on-Enter, a
// thinking indicator while the async reply is generated, auto-scroll, and
// clearing the input after each send. The loading state clears automatically
// when the broadcast appends an assistant message (detected via the
// `.message-assistant` class) so the spinner never gets stuck.
export default class extends Controller {
  static targets = [
    "messagesContainer",
    "input",
    "form",
    "submitButton",
    "submitIcon",
    "submitSpinner",
    "loadingIndicator",
  ];

  static values = { conversationId: Number };

  // If the broadcast doesn't arrive (cable hiccup, backgrounded tab), reload
  // the pane's turbo frame — the reply is already persisted server-side, so a
  // frame refresh renders it. First check after 15s, then every 15s, capped.
  static FALLBACK_INTERVAL_MS = 15000;
  static FALLBACK_MAX_ATTEMPTS = 8;

  connect() {
    this.waitingForResponse = false;
    this.scrollToBottom();
    this.setupMutationObserver();
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    this.stopFallbackPolling();
  }

  setupMutationObserver() {
    if (!this.hasMessagesContainerTarget) return;

    this.observer = new MutationObserver((mutations) => {
      this.scrollToBottom();
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeType === 1 &&
            node.classList &&
            node.classList.contains("message-assistant")
          ) {
            this.setLoadingState(false);
            this.focusInput();
          }
        });
      });
    });

    this.observer.observe(this.messagesContainerTarget, {
      childList: true,
      subtree: true,
    });
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!this.waitingForResponse) this.formTarget.requestSubmit();
    }
  }

  selectOption(event) {
    event.preventDefault();
    if (this.waitingForResponse) return;

    const text = event.currentTarget.dataset.optionText;
    if (text && this.hasInputTarget && this.hasFormTarget) {
      this.inputTarget.value = text;
      this.formTarget.requestSubmit();
    }
  }

  submitStart() {
    this.setLoadingState(true);
  }

  submitEnd(event) {
    if (event.detail && event.detail.success && this.hasInputTarget) {
      this.inputTarget.value = "";
    }
    // Keep the thinking indicator up until the assistant reply arrives.
  }

  setLoadingState(isLoading) {
    this.waitingForResponse = isLoading;

    if (isLoading) {
      this.startFallbackPolling();
    } else {
      this.stopFallbackPolling();
    }

    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = isLoading;
    if (this.hasSubmitIconTarget) this.submitIconTarget.classList.toggle("d-none", isLoading);
    if (this.hasSubmitSpinnerTarget) this.submitSpinnerTarget.classList.toggle("d-none", !isLoading);
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.toggle("d-none", !isLoading);
    if (this.hasInputTarget) this.inputTarget.disabled = isLoading;
  }

  startFallbackPolling() {
    this.stopFallbackPolling();
    this.fallbackAttempts = 0;
    this.fallbackTimer = setInterval(() => {
      if (!this.waitingForResponse) {
        this.stopFallbackPolling();
        return;
      }

      this.fallbackAttempts += 1;
      if (this.fallbackAttempts > this.constructor.FALLBACK_MAX_ATTEMPTS) {
        this.stopFallbackPolling();
        this.setLoadingState(false);
        return;
      }

      const frame = this.element.closest("turbo-frame");
      if (frame && frame.src) frame.reload();
    }, this.constructor.FALLBACK_INTERVAL_MS);
  }

  stopFallbackPolling() {
    if (this.fallbackTimer) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  focusInput() {
    if (this.hasInputTarget) this.inputTarget.focus();
  }

  scrollToBottom() {
    if (this.hasMessagesContainerTarget) {
      this.messagesContainerTarget.scrollTop = this.messagesContainerTarget.scrollHeight;
    }
  }
}
